import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link, useParams, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ✅ Utility function to calculate required width for a single line
const calculateColumnWidth = (headerName, minWidth = 120) => {
  const calculatedWidth = Math.ceil(headerName.length * 8.5) + 50;
  return Math.max(minWidth, calculatedWidth);
};

// ✅ Color definitions matching your MUI theme
const COLORS = {
  blockGroup: "FFD9EAD3",        // Green background
  summaryGroup: "FFD9EAD3",      // Green background
  saplingsGroup: "FFF1C27D",     // Orange background
  headerBackground: "FFE8EAF6",  // Light purple for column headers
  headerFont: "00000000",        // Black font
  border: "FFBDBDBD",            // Light gray border
};

const BlockReport = () => {
  const [reportData, setReportData] = useState([]);
  const [allSpeciesList, setAllSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false); // ✅ Added exporting state
  const { token } = useUser();
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    axios
      .get(`/api/center/blocksummary/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReportData(res.data.blocks || []);
        setAllSpeciesList(res.data.allSpecies || []);
      })
      .catch((err) => console.error("Error fetching block data:", err))
      .finally(() => setLoading(false));
  }, [token, id]);

  const { columns, columnGroupingModel } = useMemo(() => {
    const baseColumns = [
      {
        field: "block_name",
        headerName: "Block Name",
        // ✅ Fixed duplicate width key from original code
        width: isMobile ? 220 : 180,
        renderCell: (params) => (
          <Link
            to={`/block/${params.row.block_id}/centers`}
            style={{ textDecoration: "none", color: "#1976d2", fontWeight: "500" }}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: "production_center_count",
        headerName: "Prod. Centers",
        width: 150,
      },
    ];

    const summaryColumns = [
      { field: "species_count", headerName: "Species Count", width: 150 },
      { field: "total_target", headerName: "Target", width: 120 },
      { field: "total_stock_saplings", headerName: "Stock", width: 120 },
      { field: "total_selled", headerName: "Total Sold", width: 140 },
      { field: "total_selled_price", headerName: "Total Sold Price", width: 170 },
    ];

    // ✅ Apply dynamic width calculation to species columns
    const speciesColumns = allSpeciesList.map((s) => {
      const name = s.name || s.species_name || "";
      return {
        field: `species_${s.id}`,
        headerName: name,
        width: calculateColumnWidth(name, 120),
        type: "number",
        valueGetter: (value, row) => {
          return row?.saplingLookup?.[String(s.id)]?.qty || 0;
        },
      };
    });

    const groups = [
      {
        groupId: "Summary",
        children: summaryColumns.map((c) => ({ field: c.field })),
        headerClassName: "summary-group-header",
      },
      {
        groupId: "Saplings",
        children: allSpeciesList.map((s) => ({ field: `species_${s.id}` })),
        headerClassName: "saplings-group-header",
      },
    ];

    return {
      columns: [...baseColumns, ...summaryColumns, ...speciesColumns],
      columnGroupingModel: groups,
    };
  }, [allSpeciesList, isMobile]);

  // ✅ Excel Export Function with Styling
  const exportToExcel = useCallback(async () => {
    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Block Report", {
        properties: { tabColor: { argb: "FF1976D2" } },
      });

      // Define columns for export
      const baseColumns = [
        { header: "Block Name", field: "block_name" },
        { header: "Prod. Centers", field: "production_center_count" },
      ];

      const summaryColumns = [
        { header: "Species Count", field: "species_count" },
        { header: "Target", field: "total_target" },
        { header: "Stock", field: "total_stock_saplings" },
        { header: "Total Sold", field: "total_selled" },
        { header: "Total Sold Price", field: "total_selled_price" },
      ];

      const speciesColumns = allSpeciesList.map((s) => ({
        header: s.name || s.species_name || "",
        field: `species_${s.id}`,
        getValue: (row) => row?.saplingLookup?.[String(s.id)]?.qty || 0,
      }));

      const allColumns = [...baseColumns, ...summaryColumns, ...speciesColumns];

      // Calculate column positions
      const baseColCount = baseColumns.length;
      const summaryColCount = summaryColumns.length;
      const speciesColCount = speciesColumns.length;

      const applyBorder = (cell, sides = {}) => {
        cell.border = {
          top: sides.top || { style: "thin", color: { argb: COLORS.border } },
          bottom: sides.bottom || { style: "thin", color: { argb: COLORS.border } },
          left: sides.left || { style: "thin", color: { argb: COLORS.border } },
          right: sides.right || { style: "thin", color: { argb: COLORS.border } },
        };
      };

      // ============================================
      // ROW 1: Group Headers (Merged Cells)
      // ============================================

      // Block Info Group (columns 1-2)
      worksheet.mergeCells(1, 1, 1, baseColCount);
      const blockGroupCell = worksheet.getCell(1, 1);
      blockGroupCell.value = "Block Info";
      blockGroupCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.blockGroup } };
      blockGroupCell.font = { bold: true, size: 12, color: { argb: COLORS.headerFont } };
      blockGroupCell.alignment = { horizontal: "center", vertical: "middle" };
      applyBorder(blockGroupCell);
      for (let col = 2; col <= baseColCount; col++) applyBorder(worksheet.getCell(1, col));

      // Summary Group (columns 3-7)
      const summaryStart = baseColCount + 1;
      const summaryEnd = baseColCount + summaryColCount;
      worksheet.mergeCells(1, summaryStart, 1, summaryEnd);
      const summaryGroupCell = worksheet.getCell(1, summaryStart);
      summaryGroupCell.value = "Summary";
      summaryGroupCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.summaryGroup } };
      summaryGroupCell.font = { bold: true, size: 12, color: { argb: COLORS.headerFont } };
      summaryGroupCell.alignment = { horizontal: "center", vertical: "middle" };
      applyBorder(summaryGroupCell);
      for (let col = summaryStart + 1; col <= summaryEnd; col++) applyBorder(worksheet.getCell(1, col));

      // Saplings Group (remaining columns)
      const saplingsStart = summaryEnd + 1;
      const saplingsEnd = summaryEnd + speciesColCount;
      if (speciesColCount > 0) {
        worksheet.mergeCells(1, saplingsStart, 1, saplingsEnd);
        const saplingsGroupCell = worksheet.getCell(1, saplingsStart);
        saplingsGroupCell.value = "Saplings";
        saplingsGroupCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.saplingsGroup } };
        saplingsGroupCell.font = { bold: true, size: 12, color: { argb: COLORS.headerFont } };
        saplingsGroupCell.alignment = { horizontal: "center", vertical: "middle" };
        applyBorder(saplingsGroupCell);
        for (let col = saplingsStart + 1; col <= saplingsEnd; col++) applyBorder(worksheet.getCell(1, col));
      }

      // ============================================
      // ROW 2: Column Headers
      // ============================================
      const headerRow = worksheet.addRow(allColumns.map((c) => c.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerBackground } };
        cell.font = { bold: true, size: 10, color: { argb: COLORS.headerFont } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: false };
        applyBorder(cell, { bottom: { style: "medium", color: { argb: "FF666666" } } });
      });

      // ============================================
      // DATA ROWS
      // ============================================
      reportData.forEach((row, rowIndex) => {
        const rowData = allColumns.map((col) => {
          if (col.getValue) return col.getValue(row);
          return row[col.field];
        });
        const dataRow = worksheet.addRow(rowData);

        const isEven = rowIndex % 2 === 0;
        const rowFill = isEven
          ? { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }
          : { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };

        dataRow.eachCell((cell, colNumber) => {
          cell.fill = rowFill;
          applyBorder(cell);

          if (typeof cell.value === "number") {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            if (colNumber === summaryEnd) {
              cell.numFmt = "#,##0.00"; // Currency format
            } else {
              cell.numFmt = "#,##0"; // Number format
            }
          } else {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          }
        });
      });

      // ============================================
      // COLUMN WIDTHS & FEATURES
      // ============================================
      worksheet.columns.forEach((column, index) => {
        const header = allColumns[index].header;
        let maxWidth = header.length + 4;
        const sampleRows = reportData.slice(0, 10);
        sampleRows.forEach((row) => {
          const col = allColumns[index];
          const value = col.getValue ? col.getValue(row) : row[col.field];
          if (value !== null && value !== undefined) {
            maxWidth = Math.max(maxWidth, String(value).length + 2);
          }
        });
        column.width = Math.min(maxWidth * 1.2, 30);
      });

      worksheet.views = [{ state: "frozen", xSplit: 2, ySplit: 2, topLeftCell: "C3" }];
      worksheet.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: allColumns.length } };

      // ============================================
      // DOWNLOAD
      // ============================================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Block_Report_District_${id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Failed to export Excel file. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [reportData, allSpeciesList, id]);

  return (
    <Box
      sx={{
        height: isMobile ? "calc(100vh - 56px)" : "calc(100vh - 60px)",
        display: "flex",
        flexDirection: "column",
        p: isMobile ? 1 : 2,
        backgroundColor: "#f4f6f8",
        overflow: "hidden",
      }}
    >
      {/* ✅ Responsive Toolbar: Stacks vertically on mobile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          mb: 1,
          alignItems: isMobile ? "stretch" : "center",
          flexShrink: 0,
          gap: isMobile ? 1 : 0,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by Block..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            width: isMobile ? "100%" : "300px",
            backgroundColor: "white",
            borderRadius: "4px",
          }}
        />
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "space-between" : "flex-end",
          }}
        >
          {/* ✅ Updated Export Button */}
          <Button
            variant="contained"
            color="success"
            startIcon={!isMobile && <FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={exporting || loading}
            sx={{ flexGrow: isMobile ? 1 : 0 }}
          >
            {exporting ? "Exporting..." : "Download Excel"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(-1)}
            sx={{ flexGrow: isMobile ? 1 : 0 }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "white",
          borderRadius: 1,
          boxShadow: 3,
          overflow: "hidden",
          position: "relative",
          minWidth: 0,
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={reportData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          loading={loading}
          density={isMobile ? "compact" : "standard"}
          rowHeight={isMobile ? 50 : 38}
          autoHeight={false}
          disableRowSelectionOnClick
          filterModel={{
            items: [{ id: 1, field: "block_name", operator: "contains", value: searchText }],
          }}
          sx={{
            border: "none",
            width: "100%",
            height: "100%",
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.8rem" : "0.875rem",
            },
            "& .saplings-group-header .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#f1c27d",
              justifyContent: "center",
            },
            "& .summary-group-header .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#d9ead3",
              justifyContent: "center",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto !important",
            },
            "& .MuiDataGrid-main": {
              overflow: "hidden",
            },
            "& .MuiDataGrid-columnHeaders": {
              overflow: "hidden",
            },
          }}
          slots={{ toolbar: isMobile ? null : GridToolbar }}
          pinnedColumns={{
            left: ["block_name", "production_center_count"],
            right: isMobile
              ? []
              : [
                  "species_count",
                  "total_target",
                  "total_stock_saplings",
                  "total_selled",
                  "total_selled_price",
                ],
          }}
          scrollbarSize={10}
        />
      </Box>
    </Box>
  );
};

export default BlockReport;